import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, CardHeader, Checkbox, Divider, FormControl, FormControlLabel, 
  FormGroup, FormHelperText, Grid, InputLabel, Link, MenuItem, OutlinedInput, Stack, Tab, Tabs,
  TextField as MuiTextField, Typography, Chip} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from "@mui/icons-material/Info";
import { useDeleteWithConfirmController, useEditContext, useRecordContext } from "ra-core";
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Editor} from "@tinymce/tinymce-react";
import * as Yup from 'yup';
import Edit from "~/controller/Edit";
import WithSave from "~/controller/WithSave";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { CloudProvider, CustomKeyVaultDto, CustomKeyVaultInput, SettingDto } from "~/gen/api/securefile";
import { TabContext, TabPanel } from "@mui/lab";
import { useFormik } from "formik";
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import SelectField from "~/components/formik/SelectField";
import Confirm from "~/components/widgets/Confirm";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { useRevealPass } from "~/utils/useRevealPass";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";


const basicResource = 'settings-clients-securefile-config'
const customKeyResource = 'settings-clients-securefile-customkey-config'

interface SecureFileSettingsProps {
  open: boolean;
  onClose: () => void;
}

type TabOption = 'basic' | 'custom'

const SecureFileSettings: FC<SecureFileSettingsProps> = ({
  open, onClose,
}) => {
  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")
  const [states, setStates] = useState({
    currTab: 'basic' as TabOption
  })
  const tab1Guard = useRef(() => Promise.resolve(true))
  const tab2Guard = useRef(() => Promise.resolve(true))
  
  if (!open) { return null }
  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader title="SecureFile Settings" sx={{ pb: 0 }} />
      <CardContent sx={{ pt: 1,
        '& .MuiTabPanel-root': { px: 0, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }
      }}>
        <TabContext value={states.currTab}>
          <Tabs
            indicatorColor="secondary"
            textColor="secondary"
            value={states.currTab}
            onChange={async (e, newVal: TabOption) => { 
              const pass = await (
                  states.currTab === 'basic'  ? tab1Guard 
                : states.currTab === 'custom' ? tab2Guard 
                : tab1Guard).current();
              if (pass) { setStates(v => ({...v, currTab: newVal })) }
            }}
          >
            <Tab label="Basic" value="basic" />
            <Tab label="Custom Key Vault" value="custom" />
          </Tabs>
          <Divider />
          
          <TabPanel value="basic">
            <Edit resource={basicResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
              <BasicSettings onClose={onClose} tabGuard={tab1Guard} />
            </Edit>
          </TabPanel>
          <TabPanel value="custom">
            <Edit resource={customKeyResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
              <CustomKeyVaultSettings onClose={onClose} tabGuard={tab2Guard} />
            </Edit>
          </TabPanel>

        </TabContext>
      </CardContent>
    </Card>
  )
}

export default SecureFileSettings;


interface SfSettings {
  efsEnabled: boolean;
  showLegalWarning: boolean;
  decryptionLegalWarning: string;
  offlineEnabled: boolean;
  offlineAutoRemove: boolean;
  maxOfflineTimes: number;
  maxOfflineDays: number;
  autoDiscoveryEnabled: boolean;
  scanIntervalHours: number;
  scanRootFolders: string[];
  submit: any;
}

const BasicSettings: FC<{
  onClose:()=>void,
  tabGuard: MutableRefObject<() => Promise<boolean>>,
}> = ({onClose, tabGuard}) => {
  const record : SettingDto|undefined = useRecordContext() as any
  const { loaded } = useEditContext()
  const [states, setStates] = useState({
    settings: {
      efsEnabled: record?.efsEnabled ?? true,
      showLegalWarning: record?.showLegalWarning ?? false,
      decryptionLegalWarning: record?.decryptionLegalWarning ?? '',
      offlineEnabled: record?.offlineEnabled ?? false,
      offlineAutoRemove: record?.offlineAutoRemove ?? true,
      maxOfflineTimes: record?.maxOfflineTimes ?? 200,
      maxOfflineDays: record?.maxOfflineDays ?? 30,
      autoDiscoveryEnabled: record?.autoDiscoveryEnabled ?? true,
      scanIntervalHours: record?.scanIntervalHours ?? 0.5,
      scanRootFolders: record?.scanRootFolders ?? [],
    },
    tmpRootFolder: '',
    touched: null as {[k in keyof SfSettings]+?: boolean}|null,
    errors: null as {[k in keyof SfSettings]+?: string}|null,
    showGuardAlert: false,
  })
  useEffect(() => {
    if (record) {
      setStates(v => ({...v,
        settings: {
          efsEnabled: record.efsEnabled ?? true,
          showLegalWarning: record?.showLegalWarning ?? false,
          decryptionLegalWarning: record.decryptionLegalWarning ?? '',
          offlineEnabled: record?.offlineEnabled ?? false,
          offlineAutoRemove: record?.offlineAutoRemove ?? true,
          maxOfflineTimes: record?.maxOfflineTimes ?? 200,
          maxOfflineDays: record?.maxOfflineDays ?? 30,
          autoDiscoveryEnabled: record?.autoDiscoveryEnabled ?? true,
          scanIntervalHours: record?.scanIntervalHours ?? 0.5,
          scanRootFolders: record?.scanRootFolders ?? [],
        }
      }))
    }
  }, [record])

  const validate = (s1: typeof states) => {
    const errors: any[] = []
    if (s1.settings.showLegalWarning && !s1.settings.decryptionLegalWarning) {
      errors.push({ decryptionLegalWarning: 'Legal Warning text is required' })
    } 
    if (s1.settings.offlineEnabled && !s1.settings.maxOfflineTimes) {
      errors.push({ maxOfflineTimes: 'Maximum Offline Operations is required' })
    }
    if (s1.settings.offlineEnabled && !s1.settings.maxOfflineDays) {
      errors.push({ maxOfflineDays: 'Maximum Offline Validity Period is required' })
    }
    if (s1.settings.autoDiscoveryEnabled && s1.settings.scanRootFolders.length === 0) {
      errors.push({ scanRootFolders: 'Scan Root Folders is required' })
    }
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }
  tabGuard.current = () => new Promise<boolean>((resolve) => {
    setStates(v => {
      let pass = !Boolean(v.touched)
      if (!pass) {
        // discard edits will restore 'legal warning', in turn will trigger editor to set 'touched'
        if (Object.keys(v.touched ?? {}).length === 1 
        && v.touched?.decryptionLegalWarning 
        && v.settings.decryptionLegalWarning === record?.decryptionLegalWarning) {
          pass = true
        }
      }
      resolve(pass)
      if (pass && !v.showGuardAlert) { return v }
      else { return ({...v, showGuardAlert: !pass})}
    })
  })

  const rootFolderInputRef = useRef<HTMLInputElement>()
  
  return (
    <>
      {states.showGuardAlert &&
        <FormHelperText sx={{ color: theme => theme.palette.warning.main }}>There are unsaved changes, please make sure you have saved your edits.</FormHelperText>}
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box sx={{ m: '1px', '& div.MuiPaper-root.MuiAccordion-root': { borderRadius: 'unset' } }}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel0-content"
                id="panel0-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  EFS</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl sx={{ mt: -2 }}>
                  <FormControlLabel 
                    control={
                      <Checkbox color="primary" name="cbEfsEnabled"
                        checked={states.settings.efsEnabled}
                        onChange={(e) => setStates(v => ({...v, 
                          touched: {...v.touched, efsEnabled: true},
                          settings: {...v.settings, efsEnabled: e.target.checked}
                        }))}
                      />
                    }
                    label="Using EFS"
                  />
                  <FormHelperText>
                    Whether or not the SecureFile app should expect to use the EFS (Encrypted File System) function provided on the client machine. 
                  </FormHelperText>
                </FormControl>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  Decryption Legal Warning</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl component="fieldset" variant="standard" sx={{ width: '100%'}}>
                  <FormGroup sx={{ mb: 1 }}>
                    <FormControlLabel 
                      control={
                        <Checkbox color="primary" name=""
                          checked={states.settings.showLegalWarning}
                          onChange={(e) => setStates(v => {
                            const v1 = ({...v, 
                              touched: {...v.touched, showLegalWarning: true},
                              settings: {...v.settings, showLegalWarning: e.target.checked}
                            })
                            v1.errors = validate(v1)
                            return v1
                          })}
                        />
                      }
                      label="Show Legal Warning on SecureFile App"
                    />
                  </FormGroup>
                  {loaded &&
                    <Box sx={{ 
                      visibility: (states.settings.showLegalWarning || Boolean(states.settings.decryptionLegalWarning))
                        ? 'visible' : 'hidden',
                      display: (states.settings.showLegalWarning || Boolean(states.settings.decryptionLegalWarning))
                        ? 'block' : 'none'
                    }}>
                      <Editor 
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        init={{
                          branding: false,
                          promotion: false,
                          plugins: 'pagebreak lists wordcount',
                          menubar: false,
                          height: 530,
                          toolbar: "undo redo styles fontfamily fontsize | bold italic underline strikethrough hr | align numlist bullist | lineheight outdent indent| forecolor backcolor removeformat pagebreak",
                        }}
                        disabled={!states.settings.showLegalWarning}
                        initialValue={record?.decryptionLegalWarning ?? ''}
                        value={states.settings.decryptionLegalWarning}
                        onEditorChange={(value, editor) => {
                          setStates(v => {
                            const v1 = ({...v,
                              touched: {...v.touched, decryptionLegalWarning: true},
                              settings: {...v.settings, 
                                decryptionLegalWarning: value
                              }
                            })
                            v1.errors = validate(v1)
                            return v1
                          })
                        }}
                      />
                    </Box>}
                  {Boolean(states.errors?.decryptionLegalWarning && states.touched?.decryptionLegalWarning) &&
                    <FormHelperText error sx={{ ml: 2 }}>{states.errors?.decryptionLegalWarning}</FormHelperText>}
                </FormControl>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  Offline Access</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {loaded &&
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl>
                        <FormControlLabel
                          label="Enable Offline Access"
                          control={
                            <Checkbox checked={states.settings.offlineEnabled} 
                              onChange={(e) => setStates(v => ({...v,
                                touched: {...v.touched, offlineEnabled: true},
                                settings: {...v.settings, offlineEnabled: e.target.checked}
                              }))}
                            />
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl>
                        <FormControlLabel
                          label="Auto Remove Offline Access"
                          control={
                            <Checkbox checked={states.settings.offlineAutoRemove} 
                              onChange={(e) => setStates(v => ({...v,
                                touched: {...v.touched, offlineAutoRemove: true},
                                settings: {...v.settings, offlineAutoRemove: e.target.checked}
                              }))} 
                            />
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2, mt: 1 }}>
                      <MuiTextField label="Maximum Offline Operations per file" name="maxOfflineTimes" type="number" fullWidth
                        value={states.settings.maxOfflineTimes}
                        onChange={(e) => {
                          let i : number|'' = Number.parseInt(e.target.value)
                          i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                          setStates(v => ({...v,
                            settings: {...v.settings, maxOfflineTimes: i as any },
                            touched: {...v.touched, maxOfflineTimes: true},
                            errors:
                              i === '' && v.settings.offlineEnabled ? {...v.errors, maxOfflineTimes: 'Maximum Offline Operations is required'}
                              : v.errors?.maxOfflineTimes ? (delete v.errors.maxOfflineTimes, v.errors) : v.errors
                          }))
                        }}
                        error={Boolean(states.touched?.maxOfflineTimes && states.errors?.maxOfflineTimes)}
                        helperText={states.errors?.maxOfflineTimes}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <MuiTextField label="Maximum Offline Validity Period in days" name="maxOfflineDays" type="number" fullWidth
                        value={states.settings.maxOfflineDays}
                        onChange={(e) => {
                          let i : number|'' = Number.parseInt(e.target.value)
                          i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                          setStates(v => ({...v,
                            settings: {...v.settings, maxOfflineDays: i as any },
                            touched: {...v.touched, maxOfflineDays: true},
                            errors:
                              i === '' && v.settings.offlineEnabled ? {...v.errors, maxOfflineDays: 'Maximum Offline Validity Period is required'}
                              : v.errors?.maxOfflineDays ? (delete v.errors.maxOfflineDays, v.errors) : v.errors
                          }))
                        }}
                        error={Boolean(states.touched?.maxOfflineDays && states.errors?.maxOfflineDays)}
                        helperText={states.errors?.maxOfflineDays}
                      />
                    </Grid>
                  </Grid>}
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3-content"
                id="panel3-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  Auto Discovery</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl>
                      <FormControlLabel
                        label="Enable Auto Discovery"
                        control={
                          <Checkbox checked={states.settings.autoDiscoveryEnabled} 
                            onChange={(e) => setStates(v => ({...v,
                              touched: {...v.touched, autoDiscoveryEnabled: true},
                              settings: {...v.settings, autoDiscoveryEnabled: e.target.checked}
                            }))}
                          />
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" sx={{mt:1}}>Scan Interval: {"  "}
                        <Typography variant="body2" component="span" display="inline">every{" "}</Typography>
                      </Typography>
                      <MuiTextField size="small" type="number" value={states.settings.scanIntervalHours} 
                        onChange={(e) => {
                          let n = Number(e.target.value)
                          if (isNaN(n)) { return }
                          else if (n < 0) { n = 0.5 }
                          setStates(v => ({...v,
                            settings: {...v.settings, scanIntervalHours: n},
                            touched: {...v.touched, scanIntervalHours: true},
                          }))
                        }}
                      />
                      <Typography variant="body2" sx={{mt:theme=>`${theme.spacing(1.25)}!important`}}>hours.</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <MuiTextField fullWidth inputRef={rootFolderInputRef}
                      label="Scan Root Folders"
                      InputProps={{
                        sx: { flexWrap: 'wrap' },
                        startAdornment: (states.settings.scanRootFolders.length > 0 &&
                          <Stack direction="column" alignItems="start" flex="100%" spacing={1} 
                            sx={{ mt: 1.5, mr: 1.5, 
                              '& > div.MuiChip-root': {
                                height: 'auto', minHeight: 32,
                                '& > span.MuiChip-label': {
                                  display: 'block',
                                  wordBreak: 'break-all',
                                  whiteSpace: 'break-spaces',
                                  color: '#ad5324',
                                },
                              },
                            }} 
                            onClick={(e) => {
                              if (e.currentTarget === e.target) {
                                rootFolderInputRef.current?.focus()
                              }
                            }}
                          >
                            {states.settings.scanRootFolders.map(s => (
                              <Chip key={s} label={s} onDelete={()=>{
                                setStates(v => {
                                  const i = v.settings.scanRootFolders.indexOf(s)
                                  if (i < 0) { return v }
                                  const scanRootFolders = [...v.settings.scanRootFolders]
                                  scanRootFolders.splice(i, 1)
                                  return {...v,
                                    settings: {...v.settings, scanRootFolders },
                                    touched: {...v.touched, scanRootFolders: true},
                                  }
                                })
                              }} />
                            ))}
                          </Stack>
                        )
                      }}
                      placeholder="add new Root Folder, type Enter key to confirm..."
                      error={Boolean(states.touched?.scanRootFolders && states.errors?.scanRootFolders)}
                      helperText={states.touched?.scanRootFolders && states.errors?.scanRootFolders}
                      value={states.tmpRootFolder}
                      onChange={(e)=> { setStates(v => ({...v, tmpRootFolder: e.target.value}))}}
                      onKeyDown={(e)=> {
                        if (e.code === 'Enter' || e.code === 'NumpadEnter') {
                          setStates(v => {
                            const duplicate = v.settings.scanRootFolders.indexOf(v.tmpRootFolder) > -1
                            return {...v,
                              settings: duplicate ? v.settings :
                                {...v.settings, scanRootFolders: [...v.settings.scanRootFolders, v.tmpRootFolder]},
                              tmpRootFolder: '',
                              touched: duplicate ? v.touched : {...v.touched, scanRootFolders: true},
                            }
                          })
                          e.preventDefault()
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>
      </Grid>
      {states.errors?.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {states.errors?.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ py: 2 }} >
        <Button
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => {
            setStates({
              settings: {
                efsEnabled: record?.efsEnabled ?? true,
                showLegalWarning: record?.showLegalWarning ?? false,
                decryptionLegalWarning: record?.decryptionLegalWarning ?? '',
                offlineEnabled: record?.offlineEnabled ?? false,
                offlineAutoRemove: record?.offlineAutoRemove ?? true,
                maxOfflineTimes: record?.maxOfflineTimes ?? 200,
                maxOfflineDays: record?.maxOfflineDays ?? 30,
                autoDiscoveryEnabled: record?.autoDiscoveryEnabled ?? true,
                scanIntervalHours: record?.scanIntervalHours ?? 0.5,
                scanRootFolders: record?.scanRootFolders ?? [],
              },
              tmpRootFolder: '',
              touched: null as {[k in keyof SfSettings]+?: boolean}|null,
              errors: null as {[k in keyof SfSettings]+?: string}|null,
              showGuardAlert: false,
            })
          }}
        >
          Discard
        </Button>
        <WithSave 
          render={({ save, saving }) =>
            <Button
              color="primary"
              disabled={!states.touched || saving}
              type="submit"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setStates(v => {
                  const v1 = {...v,
                    touched: {
                      efsEnabled: true,
                      showLegalWarning: true,
                      decryptionLegalWarning: true,
                      offlineEnabled: true,
                      offlineAutoRemove: true,
                      maxOfflineTimes: true,
                      maxOfflineDays: true,
                      autoDiscoveryEnabled: true,
                      scanIntervalHours: true,
                      scanRootFolders: true,
                    },
                    showGuardAlert: false,
                  }
                  v1.errors = validate(v1)
                  if (!v1.errors) {  
                    setTimeout(() => {
                      save({...v1.settings}, {
                        onSuccess: () => {
                          setStates(v2 => ({...v2, touched: null}))
                        },
                        onFailure: (err:any) => {
                          setStates(v2 => ({...v2, errors: { submit: err.message }}))
                        },
                      })
                    })
                  }
                  return v1
                })
                return false
              }}
            >
              Save
            </Button>
          }
        />
      </Stack>
    </>
  )
}


const validationSchema =
  Yup.object().shape({
    cloudProvider: Yup.number().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.number().required("Cloud Provider is a required field")
    }),
    tenantId: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Tenant Id must be at most 100 characters').required("Tenant Id is a required field")
    }),
    clientId: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Client Id must be at most 100 characters').required("Client Id is a required field")
    }),
    clientSecret: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(65536, 'Client Secret must be at most 65536 characters').required("Client Secret is a required field")
    }),
    uri: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Uri must be at most 100 characters').required("Uri is a required field")
    }),
  })
const maskedString = '************************'

const CustomKeyVaultSettings : FC<{
  onClose:()=>void,
  tabGuard: MutableRefObject<() => Promise<boolean>>,
}> = ({onClose, tabGuard}) => {
  const record : CustomKeyVaultDto|undefined = useRecordContext() as any
  const save = useSimpleSaveAction()
  const { saving, refetch } = useEditContext()
  const [states, setStates] = useState({
    showGuardAlert: false,
  })
  const initialValues = {
    submit: '',
    isEnabled: record?.isEnabled ?? false,
    cloudProvider: record?.cloudProvider ?? '' as CloudProvider|'',
    tenantId: record?.tenantId ?? '',
    clientId: record?.clientId ?? '',
    clientSecret: record?.clientId ? maskedString : '',
    uri: record?.uri ?? '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  })=>{
    const payload: CustomKeyVaultInput = {...values} as any
    if (payload.clientSecret === maskedString) { delete payload.clientSecret }
    save(payload, {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        refetch && refetch()
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        const errMsg = retrieveAxiosErr(err)
        setErrors({ submit: errMsg })
        setSubmitting(false)
      },
    })
  }, [save, refetch])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { values, errors, touched, handleChange, handleBlur, setFieldTouched, resetForm, 
    submitForm, setStatus, setErrors, setSubmitting } = formikCtrl
  useEffect(() => {
    if (record) {
      resetForm({
        values: {
          submit: '',
          isEnabled: record.isEnabled ?? false,
          cloudProvider: record.cloudProvider ?? '' as CloudProvider|'',
          tenantId: record.tenantId ?? '',
          clientId: record.clientId ?? '',
          clientSecret: record?.clientId ? maskedString : '',
          uri: record.uri ?? '',
        }
      })
    }
  }, [record, resetForm])
  
  const { showPass, endAdornment } = useRevealPass()
  const delHandlers = useDefaultSavedHandlers('Disable Custom Key Vault')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))
  delHandlers.onFailure.merge(useCallback((err:any) => {
    setStatus({ success: false })
    const errMsg = retrieveAxiosErr(err)
    setErrors({ submit: errMsg })
    setSubmitting(false)
  }, [setErrors, setStatus, setSubmitting]))
  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource: customKeyResource,
    record: (record as any),
    mutationMode: 'pessimistic',
    ...delHandlers,
  });
  
  tabGuard.current = () => new Promise<boolean>((resolve) => {
    let pass = Object.keys(touched ?? {}).length === 0
    resolve(pass)
    if (!pass || states.showGuardAlert) {
      setStates(v => ({...v, showGuardAlert: !pass}))
    }
  })
  return (
    <>
      {states.showGuardAlert &&
        <FormHelperText sx={{ color: theme => theme.palette.warning.main }}>There are unsaved changes, please make sure you have saved your edits.</FormHelperText>}
      <OdForm formikCtrl={formikCtrl}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormControl>
              <FormControlLabel 
                control={
                  <Checkbox color="primary" name="isEnabled"
                    checked={values.isEnabled}
                    onChange={(e:any) => {
                      setFieldTouched('isEnabled', true)
                      handleChange(e)
                    }}
                  />
                }
                label="Enable Custom Key Vault"
              />
              <FormHelperText>
                Refer to {" "}
                <Link component={RouterLink} variant="caption" underline="always" 
                  to="/documents/how-to_guides/portal_configuration/use_custom_key_vault">
                  documentation</Link> for more about Custom Key Vault.
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <SelectField label="Cloud Provider*" name="cloudProvider" disabled={!values.isEnabled}>
              <MenuItem key={CloudProvider.Azure} value={CloudProvider.Azure}>Azure</MenuItem>
              <MenuItem key={CloudProvider.AzureUSGovernment} value={CloudProvider.AzureUSGovernment}>Azure US Government</MenuItem>
            </SelectField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Tenant Id*" name="tenantId" disabled={!values.isEnabled} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Client Id*" name="clientId" disabled={!values.isEnabled} />
          </Grid>
          <Grid item xs={12}>
            <FormControl 
              fullWidth 
              variant="outlined"
              error={Boolean(touched.clientSecret && errors.clientSecret)}
            >
              <InputLabel htmlFor="outlined-adornment-password">Client Secret*</InputLabel>
              <OutlinedInput id="outlined-adornment-password" name="clientSecret"
                label="Client Secret*"
                disabled={!values.isEnabled}
                type={touched.clientSecret && showPass ? 'text' : 'password'}
                value={values.clientSecret}
                onBlur={handleBlur}
                onChange={(e:any) => { setFieldTouched('clientSecret', true); handleChange(e)}}
                endAdornment={touched.clientSecret ? endAdornment : null}
                sx={{ '& input[type=password]::-ms-reveal': { display: 'none' } }}
              />
              <FormHelperText>{touched.clientSecret && errors.clientSecret}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Uri*" name="uri" disabled={!values.isEnabled} />
          </Grid>
        </Grid>
      </OdForm>
        
      {errors.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {errors.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ py: 2 }} >
        <Button
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => { 
            setStates(v => ({...v, showGuardAlert: false}))            
            resetForm({touched:undefined}) 
          }}
        >
          Discard
        </Button>
        <Button
          color="primary"
          disabled={Object.keys(touched ?? {}).length === 0 || saving || delSaving}
          type="submit"
          variant="contained"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            setStates(v => ({...v, showGuardAlert: false}))
            if (values.isEnabled) {
              submitForm()
            } else {
              setStates(v => ({...v, anchorEl: e.currentTarget}))
              handleDialogOpen(e)
            }
            return false
          }}
        >
          Save
        </Button>
        <Confirm
          isOpen={delConfirmOpen}
          loading={delSaving}
          valid={!values.isEnabled}
          title={"Disable Custom Key Vault"}
          onConfirm={handleDelete}
          onClose={handleDialogClose}
          content={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Disable Custom Key Vault to use encryption keys managed by OnData?
              </Typography>
            </Box>
          }
        />
      </Stack>
    </>
  )
}



